import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { AlertTriangle, ArrowLeft, BarChart3, CalendarIcon, Check, ChevronDown, ChevronRight, Info, List, Package, Search, ShoppingCart, UserCheck, UserPlus, Users, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';
import Content from '~/components/layout/Content';

type ApplicationCustomerType = 'NEW_CUSTOMERS_ONLY' | 'RETURNING_CUSTOMERS_ONLY' | 'ALL_CUSTOMERS';

const customerTypeLabelMap: Record<ApplicationCustomerType, string> = {
    ALL_CUSTOMERS: 'Alle Kunden',
    NEW_CUSTOMERS_ONLY: 'Nur Neukunden',
    RETURNING_CUSTOMERS_ONLY: 'Nur Bestandskunden',
};

const formatCurrency = (value: number | string) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(num);
};

const formatMonthLabel = (monthKey: string) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return format(date, 'MMMM yyyy', { locale: de });
};

// --- Date Picker ---

type DatePickerProps = {
    date: Date | undefined;
    onSelect: (date: Date | undefined) => void;
    placeholder: string;
};

const DatePicker = ({ date, onSelect, placeholder }: DatePickerProps) => {
    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant='outline'
                    className={cn(
                        'w-full justify-start text-left font-normal',
                        !date && 'text-muted-foreground'
                    )}
                >
                    <CalendarIcon className='mr-2 h-4 w-4' />
                    {date ? format(date, 'dd.MM.yyyy', { locale: de }) : placeholder}
                </Button>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-0' align='start'>
                <Calendar
                    mode='single'
                    selected={date}
                    onSelect={onSelect}
                />
            </PopoverContent>
        </Popover>
    );
};

// --- Application Level Card ---

type ApplicationLevelCardProps = {
    selected: boolean;
    onClick: () => void;
    title: string;
    description: string;
    example: string;
    icon: React.ReactNode;
};

const ApplicationLevelCard = ({ selected, onClick, title, description, example, icon }: ApplicationLevelCardProps) => {
    return (
        <button
            type='button'
            onClick={onClick}
            className={cn(
                'flex flex-col gap-2 p-4 rounded-lg border-2 text-left transition-all',
                selected
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
            )}
        >
            <div className='flex items-center gap-2'>
                <div className={cn(
                    'flex items-center justify-center w-8 h-8 rounded-full',
                    selected ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-500'
                )}>
                    {icon}
                </div>
                <span className={cn('font-medium text-sm', selected ? 'text-primary' : 'text-gray-700')}>
                    {title}
                </span>
            </div>
            <p className='text-xs text-muted-foreground leading-relaxed'>
                {description}
            </p>
            <p className='text-xs text-muted-foreground/70 italic'>
                {example}
            </p>
        </button>
    );
};

// --- Settings Tab ---

type SettingsTabProps = {
    discountCodeId: string;
};

const SettingsTab = ({ discountCodeId }: SettingsTabProps) => {
    const navigate = useNavigate();
    const utils = trpc.useUtils();

    const { data: codeData } = trpc.discountCodes.getOne.useQuery({ id: discountCodeId });
    const { data: storesData } = trpc.discountCodes.getAllStores.useQuery();
    const stores = storesData?.data ?? [];

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [type, setType] = useState<'ABSOLUTE_DISCOUNT' | 'PERCENTAGE_DISCOUNT'>('PERCENTAGE_DISCOUNT');
    const [discountValue, setDiscountValue] = useState('');
    const [applicationLevel, setApplicationLevel] = useState<'PER_LINE_ITEM' | 'ENTIRE_PURCHASE'>('PER_LINE_ITEM');
    const [applicationCustomerType, setApplicationCustomerType] = useState<ApplicationCustomerType>('ALL_CUSTOMERS');
    const [startDate, setStartDate] = useState<Date | undefined>(undefined);
    const [endDate, setEndDate] = useState<Date | undefined>(undefined);
    const [validDaysOfWeek, setValidDaysOfWeek] = useState<number[]>([]);
    const [validTimeWindowStart, setValidTimeWindowStart] = useState('');
    const [validTimeWindowEnd, setValidTimeWindowEnd] = useState('');
    const [selectedStoreIds, setSelectedStoreIds] = useState<Set<number>>(new Set());
    const [storesInitialized, setStoresInitialized] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    // Initialize form from data
    useEffect(() => {
        if (codeData?.data && !isInitialized) {
            const code = codeData.data;
            setName(code.name);
            setDescription(code.description);
            setType(code.type as 'ABSOLUTE_DISCOUNT' | 'PERCENTAGE_DISCOUNT');
            setDiscountValue(code.discountValue.toString());
            setApplicationLevel(code.applicationLevel as 'PER_LINE_ITEM' | 'ENTIRE_PURCHASE');
            setApplicationCustomerType(code.applicationCustomerType as ApplicationCustomerType);
            setStartDate(new Date(code.startDate));
            setEndDate(new Date(code.endDate));
            setValidDaysOfWeek(code.validDaysOfWeek ?? []);
            setValidTimeWindowStart(code.validTimeWindowStart ?? '');
            setValidTimeWindowEnd(code.validTimeWindowEnd ?? '');
            setIsInitialized(true);
        }
    }, [codeData, isInitialized]);

    // Initialize store selection once both the code and the store list are loaded.
    // An empty validStores set means the code is valid at all stores, so we
    // pre-select every store in the UI to reflect that default.
    useEffect(() => {
        if (codeData?.data && stores.length > 0 && !storesInitialized) {
            const associations = codeData.data.validStores ?? [];
            if (associations.length === 0) {
                setSelectedStoreIds(new Set(stores.map((s) => s.id)));
            } else {
                setSelectedStoreIds(new Set(associations.map((v) => v.store.id)));
            }
            setStoresInitialized(true);
        }
    }, [codeData, stores, storesInitialized]);

    const updateMutation = trpc.discountCodes.update.useMutation({
        onSuccess: () => {
            toast.success('Rabattcode aktualisiert');
            utils.discountCodes.getOne.invalidate({ id: discountCodeId });
            utils.discountCodes.getAll.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    const deleteMutation = trpc.discountCodes.delete.useMutation({
        onSuccess: () => {
            toast.success('Rabattcode gelöscht');
            utils.discountCodes.getAll.invalidate();
            navigate('/cms/discount-codes');
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    const setStoresMutation = trpc.discountCodes.setStores.useMutation({
        onSuccess: () => {
            toast.success('Standorte aktualisiert');
            utils.discountCodes.getOne.invalidate({ id: discountCodeId });
            utils.discountCodes.getAll.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    const toggleStore = (storeId: number) => {
        setSelectedStoreIds((prev) => {
            const next = new Set(prev);
            if (next.has(storeId)) {
                next.delete(storeId);
            } else {
                next.add(storeId);
            }
            return next;
        });
    };

    const allStoresSelected = stores.length > 0 && stores.every((s) => selectedStoreIds.has(s.id));

    const handleSaveStores = () => {
        setStoresMutation.mutate({
            discountCodeId,
            storeIds: Array.from(selectedStoreIds),
        });
    };

    const handleSave = () => {
        if (!name.trim()) {
            toast.error('Bitte geben Sie einen Namen ein');
            return;
        }
        if (!discountValue || parseFloat(discountValue) <= 0) {
            toast.error('Bitte geben Sie einen gültigen Rabattwert ein');
            return;
        }
        if (type === 'PERCENTAGE_DISCOUNT' && parseFloat(discountValue) > 100) {
            toast.error('Prozentwert darf nicht über 100 liegen');
            return;
        }
        if (!startDate) {
            toast.error('Bitte wählen Sie ein Startdatum');
            return;
        }
        if (!endDate) {
            toast.error('Bitte wählen Sie ein Enddatum');
            return;
        }
        if (endDate <= startDate) {
            toast.error('Enddatum muss nach dem Startdatum liegen');
            return;
        }

        updateMutation.mutate({
            id: discountCodeId,
            name: name.trim(),
            description: description.trim(),
            type,
            discountValue: parseFloat(discountValue),
            applicationLevel,
            applicationCustomerType,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            validDaysOfWeek,
            validTimeWindowStart: validTimeWindowStart || null,
            validTimeWindowEnd: validTimeWindowEnd || null,
        });
    };

    if (!codeData?.data) {
        return <Skeleton className='h-96 w-full' />;
    }

    return (
        <div className='space-y-8'>
            {/* General Settings */}
            <Card>
                <CardHeader>
                    <CardTitle className='text-lg'>Allgemeine Einstellungen</CardTitle>
                    <CardDescription>
                        Bearbeiten Sie die grundlegenden Einstellungen des Rabattcodes.
                    </CardDescription>
                </CardHeader>
                <CardContent className='space-y-4'>
                    <div className='space-y-2'>
                        <Label>Name</Label>
                        <Input
                            placeholder='z.B. SOMMER2026'
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </div>
                    <div className='space-y-2'>
                        <Label>Beschreibung</Label>
                        <Input
                            placeholder='z.B. Sommeraktion 2026 - 10% auf alle Hydrafacials'
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                        <p className='text-xs text-muted-foreground'>
                            Optionale Beschreibung zur internen Dokumentation des Rabattcodes.
                        </p>
                    </div>
                    <div className='grid grid-cols-2 gap-4'>
                        <div className='space-y-2'>
                            <Label>Rabatttyp</Label>
                            <Select
                                value={type}
                                onValueChange={(value: 'ABSOLUTE_DISCOUNT' | 'PERCENTAGE_DISCOUNT') => setType(value)}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value='PERCENTAGE_DISCOUNT'>Prozentualer Rabatt</SelectItem>
                                    <SelectItem value='ABSOLUTE_DISCOUNT'>Absoluter Rabatt (EUR)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className='space-y-2'>
                            <Label>
                                {type === 'PERCENTAGE_DISCOUNT' ? 'Prozent (%)' : 'Betrag (EUR)'}
                            </Label>
                            <Input
                                type='number'
                                step={type === 'PERCENTAGE_DISCOUNT' ? '1' : '0.01'}
                                min='0'
                                max={type === 'PERCENTAGE_DISCOUNT' ? '100' : undefined}
                                placeholder={type === 'PERCENTAGE_DISCOUNT' ? '10' : '25.00'}
                                value={discountValue}
                                onChange={(e) => setDiscountValue(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Application Level Select-Cards */}
                    <div className='space-y-2'>
                        <Label>Anwendungsebene</Label>
                        <div className='grid grid-cols-2 gap-3'>
                            <ApplicationLevelCard
                                selected={applicationLevel === 'PER_LINE_ITEM'}
                                onClick={() => setApplicationLevel('PER_LINE_ITEM')}
                                title='Pro Einzelposition'
                                description='Der Rabatt wird auf die zutreffenden Buchungspositionen angewendet und anteilig verteilt. Sie können festlegen, für welche buchbaren Services und Bundles der Rabattcode gilt.'
                                example='Beispiel: Absoluter Rabatt 20 EUR, 3 passende Behandlungen à 100 EUR → 20 EUR anteilig verteilt (je ~6,67 EUR) = 280 EUR Gesamtbetrag'
                                icon={<List className='w-4 h-4' />}
                            />
                            <ApplicationLevelCard
                                selected={applicationLevel === 'ENTIRE_PURCHASE'}
                                onClick={() => setApplicationLevel('ENTIRE_PURCHASE')}
                                title='Auf Gesamtbetrag'
                                description='Der Rabatt wird einmalig auf den Gesamtbetrag der Buchung angewendet und anteilig auf alle Positionen verteilt. Gilt automatisch für alle buchbaren Services und Bundles.'
                                example='Beispiel: Absoluter Rabatt 20 EUR, 3 Behandlungen à 100 EUR = 300 EUR → 20 EUR Rabatt = 280 EUR Gesamtbetrag'
                                icon={<ShoppingCart className='w-4 h-4' />}
                            />
                        </div>
                    </div>

                    {/* Customer Type Select-Cards */}
                    <div className='space-y-2'>
                        <Label>Kundentyp</Label>
                        <div className='grid grid-cols-3 gap-3'>
                            <ApplicationLevelCard
                                selected={applicationCustomerType === 'ALL_CUSTOMERS'}
                                onClick={() => setApplicationCustomerType('ALL_CUSTOMERS')}
                                title='Alle Kunden'
                                description='Der Rabattcode gilt für alle Kunden, unabhängig davon ob sie neu oder bestehend sind.'
                                example=''
                                icon={<Users className='w-4 h-4' />}
                            />
                            <ApplicationLevelCard
                                selected={applicationCustomerType === 'NEW_CUSTOMERS_ONLY'}
                                onClick={() => setApplicationCustomerType('NEW_CUSTOMERS_ONLY')}
                                title='Nur Neukunden'
                                description='Der Rabattcode kann nur von Neukunden verwendet werden, die noch keinen abgeschlossenen oder geplanten Termin hatten.'
                                example=''
                                icon={<UserPlus className='w-4 h-4' />}
                            />
                            <ApplicationLevelCard
                                selected={applicationCustomerType === 'RETURNING_CUSTOMERS_ONLY'}
                                onClick={() => setApplicationCustomerType('RETURNING_CUSTOMERS_ONLY')}
                                title='Nur Bestandskunden'
                                description='Der Rabattcode kann nur von Bestandskunden verwendet werden, die mindestens einen abgeschlossenen oder geplanten Termin hatten.'
                                example=''
                                icon={<UserCheck className='w-4 h-4' />}
                            />
                        </div>
                    </div>

                    <div className='grid grid-cols-2 gap-4'>
                        <div className='space-y-2'>
                            <Label>Startdatum</Label>
                            <DatePicker
                                date={startDate}
                                onSelect={(date) => setStartDate(date)}
                                placeholder='Startdatum wählen'
                            />
                        </div>
                        <div className='space-y-2'>
                            <Label>Enddatum</Label>
                            <DatePicker
                                date={endDate}
                                onSelect={(date) => setEndDate(date)}
                                placeholder='Enddatum wählen'
                            />
                        </div>
                    </div>

                    {/* Days of Week */}
                    <div className='space-y-2'>
                        <Label>Gültige Wochentage</Label>
                        <div className='flex flex-wrap gap-2'>
                            {([
                                { value: 1, label: 'Mo' },
                                { value: 2, label: 'Di' },
                                { value: 3, label: 'Mi' },
                                { value: 4, label: 'Do' },
                                { value: 5, label: 'Fr' },
                                { value: 6, label: 'Sa' },
                                { value: 7, label: 'So' },
                            ] as const).map(({ value, label }) => (
                                <button
                                    key={value}
                                    type='button'
                                    onClick={() => {
                                        const days = validDaysOfWeek.includes(value)
                                            ? validDaysOfWeek.filter((d) => d !== value)
                                            : [...validDaysOfWeek, value];
                                        setValidDaysOfWeek(days);
                                    }}
                                    className={cn(
                                        'px-3 py-1.5 rounded-md text-sm font-medium border transition-all',
                                        validDaysOfWeek.includes(value)
                                            ? 'bg-primary text-primary-foreground border-primary'
                                            : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                                    )}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        <p className='text-xs text-muted-foreground'>
                            Keine Auswahl = an allen Tagen gültig
                        </p>
                    </div>

                    {/* Time Window */}
                    <div className='grid grid-cols-2 gap-4'>
                        <div className='space-y-2'>
                            <Label>Zeitfenster von</Label>
                            <Input
                                type='time'
                                value={validTimeWindowStart}
                                onChange={(e) => setValidTimeWindowStart(e.target.value)}
                            />
                        </div>
                        <div className='space-y-2'>
                            <Label>Zeitfenster bis</Label>
                            <Input
                                type='time'
                                value={validTimeWindowEnd}
                                onChange={(e) => setValidTimeWindowEnd(e.target.value)}
                            />
                        </div>
                        <p className='text-xs text-muted-foreground col-span-2'>
                            Beide leer = keine Zeiteinschränkung. Bezieht sich auf die Startzeit des Termins.
                        </p>
                    </div>

                    <div className='flex justify-end pt-2'>
                        <Button onClick={handleSave} disabled={updateMutation.isPending}>
                            {updateMutation.isPending ? 'Speichere...' : 'Änderungen speichern'}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Stores / Locations */}
            <Card>
                <CardHeader>
                    <div className='flex items-center justify-between'>
                        <div>
                            <CardTitle className='text-lg'>Standorte</CardTitle>
                            <CardDescription>
                                Wählen Sie die Standorte, an denen dieser Rabattcode gültig ist.
                                Alle ausgewählt = an allen Standorten gültig (auch für zukünftige Standorte).
                            </CardDescription>
                        </div>
                        <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            className='h-7 text-xs'
                            onClick={() => {
                                if (allStoresSelected) {
                                    setSelectedStoreIds(new Set());
                                } else {
                                    setSelectedStoreIds(new Set(stores.map((s) => s.id)));
                                }
                            }}
                        >
                            {allStoresSelected ? 'Alle abwählen' : 'Alle auswählen'}
                        </Button>
                    </div>
                </CardHeader>
                <CardContent className='space-y-4'>
                    <div className='grid grid-cols-2 gap-2 rounded-md border border-gray-200 p-3'>
                        {stores.map((store) => (
                            <label
                                key={store.id}
                                className='flex items-center gap-2 cursor-pointer'
                            >
                                <Checkbox
                                    checked={selectedStoreIds.has(store.id)}
                                    onCheckedChange={() => toggleStore(store.id)}
                                />
                                <span className='text-sm'>{store.name}</span>
                            </label>
                        ))}
                    </div>
                    <div className='flex justify-end'>
                        <Button onClick={handleSaveStores} disabled={setStoresMutation.isPending}>
                            {setStoresMutation.isPending ? 'Speichere...' : 'Standorte speichern'}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Danger Zone */}
            <Card className='border-red-200'>
                <CardHeader>
                    <CardTitle className='text-lg text-red-600'>Gefahrenbereich</CardTitle>
                    <CardDescription>
                        Irreversible Aktionen. Bitte gehen Sie mit Bedacht vor.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Separator className='mb-4' />
                    <div className='flex items-center justify-between'>
                        <div>
                            <p className='text-sm font-medium'>Rabattcode löschen</p>
                            <p className='text-xs text-muted-foreground'>
                                Der Rabattcode wird unwiderruflich gelöscht. Bereits gewährte Rabatte bleiben bestehen.
                            </p>
                        </div>
                        <Button variant='destructive' onClick={() => setIsDeleteDialogOpen(true)}>
                            Rabattcode löschen
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Delete Confirmation Dialog */}
            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Rabattcode löschen</DialogTitle>
                        <DialogDescription>
                            Sind Sie sicher, dass Sie den Rabattcode &quot;{codeData.data.name}&quot; löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
                        </DialogDescription>
                    </DialogHeader>
                    <div className='flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg'>
                        <AlertTriangle className='h-5 w-5 text-red-600 shrink-0 mt-0.5' />
                        <p className='text-sm text-red-800'>
                            Alle Zuordnungen zu buchbaren Services und Bundles werden ebenfalls entfernt.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant='outline' onClick={() => setIsDeleteDialogOpen(false)}>
                            Abbrechen
                        </Button>
                        <Button
                            variant='destructive'
                            onClick={() => deleteMutation.mutate({ id: discountCodeId })}
                            disabled={deleteMutation.isPending}
                        >
                            {deleteMutation.isPending ? 'Lösche...' : 'Endgültig löschen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

// --- Stats Tab ---

type StatsTabProps = {
    discountCodeId: string;
};

const StatsTab = ({ discountCodeId }: StatsTabProps) => {
    const { data: statsData, isLoading } = trpc.discountCodes.getStats.useQuery({ discountCodeId });

    if (isLoading) {
        return (
            <div className='space-y-4'>
                <div className='grid grid-cols-2 gap-4'>
                    <Skeleton className='h-32' />
                    <Skeleton className='h-32' />
                </div>
                <Skeleton className='h-48' />
            </div>
        );
    }

    const stats = statsData?.data;

    if (!stats) {
        return (
            <div className='py-12 text-center text-muted-foreground'>
                Statistiken konnten nicht geladen werden.
            </div>
        );
    }

    return (
        <div className='space-y-6'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <Card>
                    <CardHeader className='pb-2'>
                        <CardDescription>Gesamtanzahl Nutzungen</CardDescription>
                        <CardTitle className='text-3xl'>{stats.totalTimesUsed}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className='text-xs text-muted-foreground'>
                            Wie oft dieser Rabattcode eingesetzt wurde
                        </p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className='pb-2'>
                        <CardDescription>Gesamter Rabattbetrag</CardDescription>
                        <CardTitle className='text-3xl'>{formatCurrency(stats.totalDiscountAmount)}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className='text-xs text-muted-foreground'>
                            Gesamter gewährter Rabatt in EUR
                        </p>
                    </CardContent>
                </Card>
            </div>

            {stats.monthlyStats.length === 0 ? (
                <Card>
                    <CardContent className='py-12'>
                        <div className='text-center text-muted-foreground'>
                            <BarChart3 className='h-12 w-12 mx-auto mb-4 opacity-30' />
                            <p>Dieser Rabattcode wurde noch nicht verwendet.</p>
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Nutzung nach Monat</CardTitle>
                        <CardDescription>
                            Aufschlüsselung der Rabattcode-Nutzung über die Zeit
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className='rounded-md border border-gray-200 overflow-hidden'>
                            <Table>
                                <TableHeader className='bg-gray-50'>
                                    <TableRow>
                                        <TableHead className='font-medium text-gray-700'>Monat</TableHead>
                                        <TableHead className='font-medium text-gray-700 text-right'>Nutzungen</TableHead>
                                        <TableHead className='font-medium text-gray-700 text-right'>Rabattbetrag</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {stats.monthlyStats.map((monthStat) => (
                                        <TableRow key={monthStat.month}>
                                            <TableCell className='font-medium'>
                                                {formatMonthLabel(monthStat.month)}
                                            </TableCell>
                                            <TableCell className='text-right'>{monthStat.count}</TableCell>
                                            <TableCell className='text-right'>
                                                {formatCurrency(monthStat.totalAmount)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

// --- Bookable Items, Bundles & Products Tab (Mass Checkbox) ---

type BookableItemsAndBundlesTabProps = {
    discountCodeId: string;
    applicationLevel: 'PER_LINE_ITEM' | 'ENTIRE_PURCHASE';
};

type CategoryGroup = {
    categoryId: string;
    categoryName: string;
    items: Array<{
        id: string;
        nameDe: string;
        nameEn: string;
        type: string;
        storeAssociations: Array<{ store: { id: number; name: string } }>;
    }>;
};

const BookableItemsAndBundlesTab = ({ discountCodeId, applicationLevel }: BookableItemsAndBundlesTabProps) => {
    const [selectedBookableItemIds, setSelectedBookableItemIds] = useState<Set<string>>(new Set());
    const [initialBookableItemIds, setInitialBookableItemIds] = useState<Set<string>>(new Set());
    const [selectedBundleIds, setSelectedBundleIds] = useState<Set<string>>(new Set());
    const [initialBundleIds, setInitialBundleIds] = useState<Set<string>>(new Set());
    const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
    const [initialProductIds, setInitialProductIds] = useState<Set<string>>(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [isInitialized, setIsInitialized] = useState(false);
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

    const utils = trpc.useUtils();

    const { data: codeData, isLoading: isLoadingCode } = trpc.discountCodes.getOne.useQuery({ id: discountCodeId });
    const { data: allBookableItemsData, isLoading: isLoadingBookableItems } = trpc.discountCodes.getAllBookableItems.useQuery();
    const { data: allBundlesData, isLoading: isLoadingBundles } = trpc.discountCodes.getAllBundles.useQuery();
    const { data: allProductsData, isLoading: isLoadingProducts } = trpc.discountCodes.getAllProducts.useQuery();

    const setBookableItemsMutation = trpc.discountCodes.setBookableItems.useMutation({
        onSuccess: () => {
            utils.discountCodes.getOne.invalidate({ id: discountCodeId });
            utils.discountCodes.getAll.invalidate();
            setInitialBookableItemIds(new Set(selectedBookableItemIds));
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    const setBundlesMutation = trpc.discountCodes.setBundles.useMutation({
        onSuccess: () => {
            utils.discountCodes.getOne.invalidate({ id: discountCodeId });
            utils.discountCodes.getAll.invalidate();
            setInitialBundleIds(new Set(selectedBundleIds));
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    const setProductsMutation = trpc.discountCodes.setProducts.useMutation({
        onSuccess: () => {
            utils.discountCodes.getOne.invalidate({ id: discountCodeId });
            utils.discountCodes.getAll.invalidate();
            setInitialProductIds(new Set(selectedProductIds));
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    useEffect(() => {
        if (codeData?.data && !isInitialized) {
            const bookableItemIds = new Set(
                codeData.data.validBookableItems.map((v) => v.bookableItem.id)
            );
            const bundleIds = new Set(
                codeData.data.validBundles.map((v) => v.bundle.id)
            );
            const productIds = new Set(
                (codeData.data.validProducts ?? []).map((v) => v.product.id)
            );
            setSelectedBookableItemIds(bookableItemIds);
            setInitialBookableItemIds(bookableItemIds);
            setSelectedBundleIds(bundleIds);
            setInitialBundleIds(bundleIds);
            setSelectedProductIds(productIds);
            setInitialProductIds(productIds);
            setIsInitialized(true);
        }
    }, [codeData, isInitialized]);

    const bookableItems = allBookableItemsData?.data || [];
    const bundles = allBundlesData?.data || [];
    const products = allProductsData?.data || [];

    // Group bookable items by category
    const categoryGroups = useMemo((): CategoryGroup[] => {
        const categoryMap = new Map<string, CategoryGroup>();
        const uncategorizedItems: CategoryGroup['items'] = [];

        for (const item of bookableItems) {
            const categories = item.categoryAssociations ?? [];
            if (categories.length === 0) {
                uncategorizedItems.push(item);
            } else {
                for (const ca of categories) {
                    const catId = ca.category.id;
                    if (!categoryMap.has(catId)) {
                        categoryMap.set(catId, {
                            categoryId: catId,
                            categoryName: ca.category.nameDe,
                            items: [],
                        });
                    }
                    categoryMap.get(catId)!.items.push(item);
                }
            }
        }

        const groups = Array.from(categoryMap.values()).sort((a, b) =>
            a.categoryName.localeCompare(b.categoryName, 'de')
        );

        if (uncategorizedItems.length > 0) {
            groups.push({
                categoryId: '__uncategorized__',
                categoryName: 'Unkategorisiert',
                items: uncategorizedItems,
            });
        }

        return groups;
    }, [bookableItems]);

    // Filter groups by search query
    const filteredCategoryGroups = useMemo((): CategoryGroup[] => {
        if (!searchQuery.trim()) {
            return categoryGroups;
        }
        const query = searchQuery.toLowerCase();
        return categoryGroups
            .map((group) => ({
                ...group,
                items: group.items.filter(
                    (item) =>
                        item.nameDe.toLowerCase().includes(query) ||
                        item.nameEn.toLowerCase().includes(query)
                ),
            }))
            .filter((group) => group.items.length > 0);
    }, [categoryGroups, searchQuery]);

    const filteredBundles = useMemo(() => {
        if (!searchQuery.trim()) {
            return bundles;
        }
        const query = searchQuery.toLowerCase();
        return bundles.filter(
            (bundle) => bundle.name.toLowerCase().includes(query)
        );
    }, [bundles, searchQuery]);

    const filteredProducts = useMemo(() => {
        if (!searchQuery.trim()) {
            return products;
        }
        const query = searchQuery.toLowerCase();
        return products.filter(
            (product) => product.name.toLowerCase().includes(query)
        );
    }, [products, searchQuery]);

    const setsAreEqual = (a: Set<string>, b: Set<string>) => {
        if (a.size !== b.size) { return false; }
        for (const id of a) {
            if (!b.has(id)) { return false; }
        }
        return true;
    };

    const hasChanges = useMemo(() => {
        return !setsAreEqual(selectedBookableItemIds, initialBookableItemIds)
            || !setsAreEqual(selectedBundleIds, initialBundleIds)
            || !setsAreEqual(selectedProductIds, initialProductIds);
    }, [selectedBookableItemIds, initialBookableItemIds, selectedBundleIds, initialBundleIds, selectedProductIds, initialProductIds]);

    const toggleBookableItem = (itemId: string) => {
        setSelectedBookableItemIds((prev) => {
            const next = new Set(prev);
            if (next.has(itemId)) {
                next.delete(itemId);
            } else {
                next.add(itemId);
            }
            return next;
        });
    };

    const toggleBundle = (bundleId: string) => {
        setSelectedBundleIds((prev) => {
            const next = new Set(prev);
            if (next.has(bundleId)) {
                next.delete(bundleId);
            } else {
                next.add(bundleId);
            }
            return next;
        });
    };

    const toggleProduct = (productId: string) => {
        setSelectedProductIds((prev) => {
            const next = new Set(prev);
            if (next.has(productId)) {
                next.delete(productId);
            } else {
                next.add(productId);
            }
            return next;
        });
    };

    const toggleCategoryItems = (categoryItems: CategoryGroup['items']) => {
        const itemIds = categoryItems.map((item) => item.id);
        setSelectedBookableItemIds((prev) => {
            const next = new Set(prev);
            const allSelected = itemIds.every((id) => next.has(id));
            if (allSelected) {
                itemIds.forEach((id) => next.delete(id));
            } else {
                itemIds.forEach((id) => next.add(id));
            }
            return next;
        });
    };

    const toggleAllBundles = () => {
        const visibleIds = filteredBundles.map((b) => b.id);
        setSelectedBundleIds((prev) => {
            const next = new Set(prev);
            const allSelected = visibleIds.every((id) => next.has(id));
            if (allSelected) {
                visibleIds.forEach((id) => next.delete(id));
            } else {
                visibleIds.forEach((id) => next.add(id));
            }
            return next;
        });
    };

    const toggleAllProducts = () => {
        const visibleIds = filteredProducts.map((p) => p.id);
        setSelectedProductIds((prev) => {
            const next = new Set(prev);
            const allSelected = visibleIds.every((id) => next.has(id));
            if (allSelected) {
                visibleIds.forEach((id) => next.delete(id));
            } else {
                visibleIds.forEach((id) => next.add(id));
            }
            return next;
        });
    };

    const toggleCategoryExpanded = (categoryId: string) => {
        setExpandedCategories((prev) => {
            const next = new Set(prev);
            if (next.has(categoryId)) {
                next.delete(categoryId);
            } else {
                next.add(categoryId);
            }
            return next;
        });
    };

    const isSaving = setBookableItemsMutation.isPending || setBundlesMutation.isPending || setProductsMutation.isPending;

    const handleSave = async () => {
        try {
            await Promise.all([
                setBookableItemsMutation.mutateAsync({
                    discountCodeId,
                    bookableItemIds: Array.from(selectedBookableItemIds),
                }),
                setBundlesMutation.mutateAsync({
                    discountCodeId,
                    bundleIds: Array.from(selectedBundleIds),
                }),
                setProductsMutation.mutateAsync({
                    discountCodeId,
                    productIds: Array.from(selectedProductIds),
                }),
            ]);
            toast.success('Zuordnungen gespeichert');
        } catch {
            // Error already handled in onError callbacks
        }
    };

    if (applicationLevel === 'ENTIRE_PURCHASE') {
        return (
            <Card>
                <CardContent className='py-12'>
                    <div className='text-center text-muted-foreground'>
                        <Info className='h-12 w-12 mx-auto mb-4 opacity-30' />
                        <p className='font-medium mb-1'>Keine Zuordnung nötig</p>
                        <p className='text-sm'>
                            Dieser Rabattcode gilt für den Gesamtbetrag und wird automatisch auf alle
                            buchbaren Services, Bundles und Produkte angewendet.
                        </p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (isLoadingCode || isLoadingBookableItems || isLoadingBundles || isLoadingProducts) {
        return (
            <div className='space-y-4'>
                <Skeleton className='h-10 w-full' />
                <Skeleton className='h-64 w-full' />
            </div>
        );
    }

    const allBundlesVisible = filteredBundles.map((b) => b.id);
    const allBundlesSelected = allBundlesVisible.length > 0 && allBundlesVisible.every((id) => selectedBundleIds.has(id));
    const allProductsVisible = filteredProducts.map((p) => p.id);
    const allProductsSelected = allProductsVisible.length > 0 && allProductsVisible.every((id) => selectedProductIds.has(id));

    return (
        <div className='space-y-4'>
            <Card>
                <CardHeader>
                    <div className='flex items-center justify-between'>
                        <div>
                            <CardTitle className='text-lg'>Services, Bundles & Produkte auswählen</CardTitle>
                            <CardDescription>
                                Wählen Sie alle buchbaren Services, Bundles und Produkte aus, für die dieser Rabattcode gelten soll.
                                {' '}
                                <span className='font-medium'>
                                    {selectedBookableItemIds.size} Services, {selectedBundleIds.size} Bundles, {selectedProductIds.size} Produkte ausgewählt
                                </span>
                            </CardDescription>
                        </div>
                        <Button
                            onClick={handleSave}
                            disabled={!hasChanges || isSaving}
                        >
                            {isSaving ? (
                                'Speichere...'
                            ) : (
                                <>
                                    <Check className='h-4 w-4 mr-2' />
                                    Speichern
                                </>
                            )}
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className='relative'>
                        <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                        <Input
                            placeholder='Services, Bundles oder Produkte filtern...'
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className='pl-9'
                        />
                        {searchQuery && (
                            <Button
                                variant='ghost'
                                size='icon'
                                className='absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7'
                                onClick={() => setSearchQuery('')}
                            >
                                <X className='h-3 w-3' />
                            </Button>
                        )}
                    </div>

                    {hasChanges && (
                        <p className='text-sm text-amber-600 mt-3'>
                            Ungespeicherte Änderungen vorhanden. Klicken Sie auf &quot;Speichern&quot;, um die Änderungen zu übernehmen.
                        </p>
                    )}
                </CardContent>
            </Card>

            {/* Bookable Items Section - grouped by category */}
            {filteredCategoryGroups.length > 0 && (
                <Card>
                    <CardHeader className='py-3 px-4'>
                        <CardTitle className='text-sm font-medium'>
                            Buchbare Services ({selectedBookableItemIds.size}/{bookableItems.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent className='pt-0 px-4 pb-3 space-y-2'>
                        {filteredCategoryGroups.map((group) => {
                            const isExpanded = expandedCategories.has(group.categoryId);
                            const categoryItemIds = group.items.map((item) => item.id);
                            const selectedInCategory = categoryItemIds.filter((id) => selectedBookableItemIds.has(id)).length;
                            const allInCategorySelected = categoryItemIds.length > 0 && selectedInCategory === categoryItemIds.length;
                            const someInCategorySelected = selectedInCategory > 0 && !allInCategorySelected;

                            return (
                                <Collapsible
                                    key={group.categoryId}
                                    open={isExpanded}
                                    onOpenChange={() => toggleCategoryExpanded(group.categoryId)}
                                >
                                    <div className='border rounded-md'>
                                        <div className='flex items-center gap-3 px-3 py-2'>
                                            <Checkbox
                                                checked={allInCategorySelected ? true : someInCategorySelected ? 'indeterminate' : false}
                                                onCheckedChange={(e) => {
                                                    toggleCategoryItems(group.items);
                                                }}
                                            />
                                            <CollapsibleTrigger asChild>
                                                <button className='flex-1 flex items-center gap-2 text-left cursor-pointer hover:opacity-70 transition-opacity'>
                                                    {isExpanded ? (
                                                        <ChevronDown className='h-4 w-4 text-muted-foreground shrink-0' />
                                                    ) : (
                                                        <ChevronRight className='h-4 w-4 text-muted-foreground shrink-0' />
                                                    )}
                                                    <span className='text-sm font-medium'>{group.categoryName}</span>
                                                    <span className='text-xs text-muted-foreground'>
                                                        ({selectedInCategory}/{categoryItemIds.length})
                                                    </span>
                                                </button>
                                            </CollapsibleTrigger>
                                        </div>
                                        <CollapsibleContent>
                                            <div className='border-t divide-y'>
                                                {group.items.map((item) => (
                                                    <label
                                                        key={item.id}
                                                        className='flex items-center gap-3 px-3 py-2 pl-11 hover:bg-gray-50 cursor-pointer transition-colors'
                                                    >
                                                        <Checkbox
                                                            checked={selectedBookableItemIds.has(item.id)}
                                                            onCheckedChange={() => toggleBookableItem(item.id)}
                                                        />
                                                        <div className='flex-1 flex items-center gap-2 min-w-0'>
                                                            <span className='text-sm truncate'>{item.nameDe}</span>
                                                            <Badge variant='outline' className='text-[10px] px-1.5 py-0 h-5 shrink-0'>
                                                                {item.type === 'BUNDLE' ? 'Bundle' : 'Service'}
                                                            </Badge>
                                                            {item.storeAssociations && item.storeAssociations.length > 0 && (
                                                                <div className='flex items-center gap-1 shrink-0'>
                                                                    {item.storeAssociations.map((sa) => (
                                                                        <Badge key={sa.store.id} variant='outline' className='text-[10px] px-1.5 py-0 h-5'>
                                                                            {sa.store.name}
                                                                        </Badge>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                        </CollapsibleContent>
                                    </div>
                                </Collapsible>
                            );
                        })}
                    </CardContent>
                </Card>
            )}

            {/* Bundles Section */}
            {filteredBundles.length > 0 && (
                <Card>
                    <CardHeader className='py-3 px-4'>
                        <div className='flex items-center justify-between'>
                            <div className='flex items-center gap-3'>
                                <Checkbox
                                    checked={allBundlesSelected}
                                    onCheckedChange={toggleAllBundles}
                                />
                                <CardTitle className='text-sm font-medium'>
                                    Bundles ({selectedBundleIds.size}/{bundles.length})
                                </CardTitle>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className='pt-0 px-4 pb-3'>
                        <div className='border rounded-md divide-y'>
                            {filteredBundles.map((bundle) => (
                                <label
                                    key={bundle.id}
                                    className='flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors'
                                >
                                    <Checkbox
                                        checked={selectedBundleIds.has(bundle.id)}
                                        onCheckedChange={() => toggleBundle(bundle.id)}
                                    />
                                    <div className='flex-1 flex items-center gap-2 min-w-0'>
                                        <span className='text-sm truncate'>{bundle.name}</span>
                                    </div>
                                    <span className='text-xs text-muted-foreground shrink-0'>
                                        {formatCurrency(Number(bundle.unitPriceGross))} x{bundle.unitQuantity}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Products Section */}
            {filteredProducts.length > 0 && (
                <Card>
                    <CardHeader className='py-3 px-4'>
                        <div className='flex items-center justify-between'>
                            <div className='flex items-center gap-3'>
                                <Checkbox
                                    checked={allProductsSelected}
                                    onCheckedChange={toggleAllProducts}
                                />
                                <CardTitle className='text-sm font-medium'>
                                    Produkte ({selectedProductIds.size}/{products.length})
                                </CardTitle>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className='pt-0 px-4 pb-3'>
                        <div className='border rounded-md divide-y'>
                            {filteredProducts.map((product) => (
                                <label
                                    key={product.id}
                                    className='flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors'
                                >
                                    <Checkbox
                                        checked={selectedProductIds.has(product.id)}
                                        onCheckedChange={() => toggleProduct(product.id)}
                                    />
                                    <div className='flex-1 flex items-center gap-2 min-w-0'>
                                        <span className='text-sm truncate'>{product.name}</span>
                                        {!product.isActive && (
                                            <Badge variant='secondary' className='text-[10px] px-1.5 py-0 h-5 shrink-0'>
                                                Inaktiv
                                            </Badge>
                                        )}
                                    </div>
                                    <span className='text-xs text-muted-foreground shrink-0'>
                                        {formatCurrency(Number(product.unitPriceGross))}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {filteredCategoryGroups.length === 0 && filteredBundles.length === 0 && filteredProducts.length === 0 && (
                <Card>
                    <CardContent className='py-8'>
                        <div className='text-center text-muted-foreground'>
                            {searchQuery
                                ? 'Keine Services, Bundles oder Produkte gefunden.'
                                : 'Keine buchbaren Services, Bundles oder Produkte verfügbar.'
                            }
                        </div>
                    </CardContent>
                </Card>
            )}

            {hasChanges && (
                <div className='sticky bottom-4 z-10'>
                    <div className='bg-white border rounded-lg shadow-lg p-4 flex items-center justify-between'>
                        <span className='text-sm text-muted-foreground'>
                            {selectedBookableItemIds.size} Services, {selectedBundleIds.size} Bundles, {selectedProductIds.size} Produkte ausgewählt
                        </span>
                        <Button
                            onClick={handleSave}
                            disabled={isSaving}
                        >
                            {isSaving ? 'Speichere...' : 'Änderungen speichern'}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- Main Detail Component ---

const DiscountCodeDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    const { data: codeData, isLoading } = trpc.discountCodes.getOne.useQuery(
        { id: id! },
        { enabled: !!id }
    );

    if (!id) {
        return null;
    }

    if (isLoading) {
        return (
            <Content>
                <div className='container mx-auto py-10'>
                    <Skeleton className='h-10 w-64 mb-6' />
                    <Skeleton className='h-96 w-full' />
                </div>
            </Content>
        );
    }

    if (!codeData?.data) {
        return (
            <Content>
                <div className='container mx-auto py-10'>
                    <div className='text-center py-12 text-muted-foreground'>
                        Rabattcode nicht gefunden.
                    </div>
                </div>
            </Content>
        );
    }

    const code = codeData.data;
    const now = new Date();
    const isActive = now >= new Date(code.startDate) && now <= new Date(code.endDate);
    const isExpired = now > new Date(code.endDate);
    const isUpcoming = now < new Date(code.startDate);
    const applicationLevel = code.applicationLevel as 'PER_LINE_ITEM' | 'ENTIRE_PURCHASE';

    return (
        <Content>
            <div className='container mx-auto py-10'>
                <div className='flex items-center gap-4 mb-6'>
                    <Button variant='ghost' size='icon' onClick={() => navigate('/cms/discount-codes')}>
                        <ArrowLeft className='h-4 w-4' />
                    </Button>
                    <div className='flex-1'>
                        <div className='flex items-center gap-3'>
                            <h1 className='text-3xl font-bold'>{code.name}</h1>
                            {isActive && (
                                <Badge variant='outline' className='text-green-600 border-green-300'>Aktiv</Badge>
                            )}
                            {isExpired && (
                                <Badge variant='outline' className='text-gray-500 border-gray-300'>Abgelaufen</Badge>
                            )}
                            {isUpcoming && (
                                <Badge variant='outline' className='text-blue-600 border-blue-300'>Geplant</Badge>
                            )}
                            <Badge variant='outline' className='text-xs'>
                                {applicationLevel === 'ENTIRE_PURCHASE' ? 'Gesamtbetrag' : 'Pro Einzelposition'}
                            </Badge>
                            <Badge variant='outline' className='text-xs'>
                                {customerTypeLabelMap[code.applicationCustomerType as ApplicationCustomerType]}
                            </Badge>
                        </div>
                        <p className='text-muted-foreground'>
                            {code.type === 'PERCENTAGE_DISCOUNT'
                                ? `${code.discountValue}% Rabatt`
                                : `${formatCurrency(Number(code.discountValue))} Rabatt`
                            }
                            {' '}&middot;{' '}
                            {format(new Date(code.startDate), 'dd.MM.yyyy', { locale: de })}
                            {' bis '}
                            {format(new Date(code.endDate), 'dd.MM.yyyy', { locale: de })}
                        </p>
                        {code.description && (
                            <p className='text-sm text-muted-foreground mt-1'>{code.description}</p>
                        )}
                    </div>
                </div>

                <Tabs defaultValue='settings' className='w-full'>
                    <TabsList className='mb-6'>
                        <TabsTrigger value='settings'>Einstellungen</TabsTrigger>
                        <TabsTrigger value='items'>
                            {applicationLevel === 'ENTIRE_PURCHASE'
                                ? 'Zuordnungen'
                                : 'Services & Bundles'
                            }
                        </TabsTrigger>
                        <TabsTrigger value='stats'>Statistiken</TabsTrigger>
                    </TabsList>

                    <TabsContent value='settings'>
                        <SettingsTab discountCodeId={id} />
                    </TabsContent>

                    <TabsContent value='items'>
                        <BookableItemsAndBundlesTab
                            discountCodeId={id}
                            applicationLevel={applicationLevel}
                        />
                    </TabsContent>

                    <TabsContent value='stats'>
                        <StatsTab discountCodeId={id} />
                    </TabsContent>
                </Tabs>
            </div>
        </Content>
    );
};

export default DiscountCodeDetail;
